import React from "react";
import {
  Box,
  VStack,
  Heading,
  Text,
  Button,
  Icon,
  Grid,
  GridItem,
  Flex,
  Image,
  useColorModeValue,
} from "@chakra-ui/react";
import { FaDollarSign, FaRegHandshake, FaUserShield } from "react-icons/fa";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const campaignImage = `https://${window.location.host}/images/share-image.png`;

function Home() {
  return (
    <Box bg={useColorModeValue("#f4eee7", "gray.800")} color="#0f393b">
      <Helmet>
        <title>Welcome to PurelyGive - Empowering Nonprofits</title>
        <meta
          name="description"
          content="PurelyGive empowers nonprofits to raise funds efficiently without transaction fees. Discover how we support your cause with our zero-fee fundraising platform."
        />
        <meta
          name="keywords"
          content="nonprofit fundraising, zero fees, donations, PurelyGive, support causes"
        />
        <meta
          property="og:title"
          content="PurelyGive - Empowering Zero-Fee Fundraising"
        />
        <meta
          property="og:description"
          content="Join PurelyGive to support your favorite causes without transaction fees. Empower nonprofits today!"
        />
        <meta property="og:image" content={campaignImage} />
        <meta property="og:url" content="https://purelygive.com" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="PurelyGive - Empowering Zero-Fee Fundraising" />
        <meta
          name="twitter:description"
          content="Join PurelyGive to support your favorite causes without transaction fees. Empower nonprofits today!"
        />
        <meta name="twitter:image" content={campaignImage} />
      </Helmet>

      <VStack spacing={0} align="stretch">
        {/* Hero Section */}
        <Box
          bg="url('/images/hero_bg_min.png'), rgba(0, 0, 0, 0.65)"
          backgroundBlendMode="overlay"
          bgPosition="center"
          bgSize="cover"
          color="white"
          py={{ base: 20, md: 28 }}
          textAlign="center"
          // bg="#0f393b"
        >
          <Flex direction="column" align="center" px={6}>
            <Heading as="h1" size="2xl" mb={4}>
              Zero Fees. 100% Impact.
            </Heading>
            <Text fontSize="xl" maxW="600px">
              Every dollar donated goes directly to your cause. Join us to
              revolutionize your fundraising.
            </Text>
            <Button
              size="lg"
              mt={8}
              as={Link}
              to="/register"
              colorScheme="orange"
              px={8}
              py={6}
              _hover={{ bg: "orange.600" }}
            >
              Get Started for Free!
            </Button>
          </Flex>
        </Box>

        {/* Introducing PurelyGive */}
        <Box w="100%" py={16} px={6} bg="#f4eee7">
          <Flex
            maxW="1200px"
            mx="auto"
            direction={{ base: "column", md: "row" }}
            align="center"
            justify="space-between"
          >
            {/* Text Content */}
            <Box flex="1" mr={{ md: 8 }} mb={{ base: 8, md: 0 }}>
              <Heading as="h2" size="xl" color="#0f393b" mb={4}>
                Introducing PurelyGive
              </Heading>
              <Text fontSize="lg" color="#333">
                Our platform stands out by covering all transaction fees,
                ensuring that every cent of your donations reaches your cause.
                Imagine receiving 100% of every $100 donation—because with us,
                that’s exactly what happens. No transaction fees, no monthly
                charges, just full support for your mission.
              </Text>
              <Button
                size="md"
                mt={6}
                as={Link}
                to="/register"
                colorScheme="orange"
                px={6}
                py={4}
                _hover={{ bg: "orange.600" }}
              >
                Get Started
              </Button>
            </Box>

            {/* Video Content */}
            <Box flex="1" w="100%">
              <Box
                position="relative"
                paddingTop="56.25%" // 16:9 Aspect Ratio
                overflow="hidden"
                borderRadius="md"
              >
                <iframe
                  title="explainer video"
                  src="https://www.youtube.com/embed/Xfjr5WIs-mI?autoplay=0&controls=0&modestbranding=1&rel=0&showinfo=0"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    border: 0,
                  }}
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                />
              </Box>
            </Box>
          </Flex>
        </Box>

        {/* Features Section */}
        <Box w="100%" py={16} px={6} bg="#ffffff">
          <Heading as="h2" size="xl" textAlign="center" color="#0f393b">
            Features
          </Heading>
          <Grid
            templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
            gap={10}
            mt={12}
            maxW="1200px"
            mx="auto"
          >
            {/* Feature 1 */}
            <GridItem textAlign="center">
              <Icon as={FaDollarSign} w={12} h={12} color="orange.500" />
              <Heading as="h3" size="md" mt={4} color="#0f393b">
                0% Processing Fees
              </Heading>
              <Text mt={2} color="#666">
                We cover all transaction fees so you can keep 100% of your
                donations.
              </Text>
            </GridItem>
            {/* Feature 2 */}
            <GridItem textAlign="center">
              <Icon as={FaRegHandshake} w={12} h={12} color="orange.500" />
              <Heading as="h3" size="md" mt={4} color="#0f393b">
                No Subscription Costs
              </Heading>
              <Text mt={2} color="#666">
                Our platform is free to use with no hidden costs.
              </Text>
            </GridItem>
            {/* Feature 3 */}
            <GridItem textAlign="center">
              <Icon as={FaUserShield} w={12} h={12} color="orange.500" />
              <Heading as="h3" size="md" mt={4} color="#0f393b">
                Secure Withdrawals
              </Heading>
              <Text mt={2} color="#666">
                Access your funds confidently and securely on your own schedule.
              </Text>
            </GridItem>
          </Grid>
        </Box>

        {/* Why Zero Fees */}
        <Box w="100%" py={16} px={6} bg="#f4eee7">
          <Flex
            maxW="1200px"
            mx="auto"
            direction={{ base: "column-reverse", md: "row" }}
            align="center"
            justify="space-between"
          >
            <Box flex="1">
              <Image
                src="/images/optional_donation.png"
                alt="Zero Fees"
                borderRadius="md"
                shadow="md"
              />
            </Box>
            <Box flex="1" ml={{ md: 8 }} mb={{ base: 8, md: 0 }}>
              <Heading as="h2" size="xl" color="#0f393b" mb={4}>
                How Does PurelyGive Provide Its Incredible Services at No Cost?
              </Heading>
              <Text fontSize="lg" color="#333">
                The secret is in the generosity of our donors. At the moment of
                payment confirmation on our fundraising forms, donors are given
                the chance to make a small, optional donation to PurelyGive.
                This is our exclusive source of income, empowering us to
                eliminate all fees for the nonprofits that fundraise with us.
              </Text>
            </Box>
          </Flex>
        </Box>



        {/* Compare Platforms */}
        <Box w="100%" py={16} px={6} bg="#f4eee7">
          <Flex maxW="1200px" mx="auto" direction={{ base: "column", md: "row" }} align="center" justify="space-between" gap={8}>
            <Box>
              <Heading as="h2" size="lg" color="#0f393b" mb={3}>
                Compare PurelyGive
              </Heading>
              <Text color="#333" maxW="680px">
                See side-by-side comparisons with popular platforms and estimate how much your nonprofit could save annually.
              </Text>
            </Box>
            <Button as={Link} to="/compare" colorScheme="orange" size="lg">
              Explore comparisons
            </Button>
          </Flex>
        </Box>

        {/* Currently Supporting */}
        <Box w="100%" py={16} px={6} bg="#ffffff">
          <Heading as="h2" size="xl" textAlign="center" color="#0f393b">
            Currently Supporting
          </Heading>
          <Box
            mt={12}
            maxW="900px"
            mx="auto"
            p={{ base: 8, md: 12 }}
            borderRadius="xl"
            bg="linear-gradient(135deg, #ffffff 0%, #f9f4ee 100%)"
            border="1px solid"
            borderColor="orange.100"
            boxShadow="xl"
          >
            <Flex
              direction={{ base: "column", md: "row" }}
              align={{ base: "flex-start", md: "center" }}
              gap={8}
            >
              <Image
                src="https://o4c.org/wp-content/uploads/2019/07/logo-top.png"
                alt="Orphans For Christ logo"
                maxW={{ base: "200px", md: "240px" }}
                objectFit="contain"
              />
              <Box>
                <Text fontSize={{ base: "lg", md: "xl" }} color="#333" lineHeight="tall">
                  “PurelyGive allows us to receive 100% of online donations
                  without worrying about platform fees. The setup was simple,
                  and the transparency gives us confidence.”
                </Text>
                <Text mt={6} fontSize="md" fontWeight="bold" color="#0f393b">
                  – John, Director
                </Text>
                <Text mt={1} fontSize="md" color="#0f393b">
                  Orphans For Christ
                </Text>
              </Box>
            </Flex>
          </Box>
        </Box>
      </VStack>
    </Box>
  );
}

export default Home;
